import axios from "axios";
import { AuthApi } from "./auth";
import { CasesApi } from "./cases";
import { AdminApi } from "./admin";
import ReviewerApi from "./reviewer";

// API Configuration - Real API only, no mock data
const getApiBaseUrl = () => {
  // Always use environment variable if provided
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production, use the Vercel proxy to avoid mixed content issues
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // Development: direct HTTP call
  return 'http://16.170.221.169/api';
};

const API_BASE_URL = getApiBaseUrl();
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

console.log("🔗 API Configuration:", {
  baseURL: API_BASE_URL,
  environment: IS_DEVELOPMENT ? "development" : "production",
  mockDataDisabled: true
});

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
    // Add headers for CORS and HTTP compatibility
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
    "Access-Control-Allow-Headers": "Content-Type, Authorization"
  },
  // Handle mixed content issues
  withCredentials: false,
  // Disable SSL verification for HTTP endpoints
  ...(process.env.NODE_ENV === 'development' && {
    httpsAgent: false
  })
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage with correct key
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log API requests in development
    if (IS_DEVELOPMENT) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data
      });
    }
    
    return config;
  },
  (error) => {
    console.error("🚨 Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for standardized error handling
api.interceptors.response.use(
  (response) => {
    // Log API responses in development
    if (IS_DEVELOPMENT) {
      console.log(`✅ API Response: ${response.config.url}`, {
        status: response.status,
        success: response.data?.success,
        data: response.data
      });
    }
    
    return response;
  },
  (error) => {
    console.error("🚨 API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });

    // Handle network errors (common in Vercel with mixed content)
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      console.error("🌐 Network Error - Possible HTTPS/HTTP mixed content issue");
      console.error("Current API URL:", API_BASE_URL);
      console.error("Suggestion: Ensure API server supports HTTPS");
    }

    // Handle auth errors - but let auth context handle token clearing
    if (error.response?.status === 401) {
      console.warn("🔐 Unauthorized request detected - auth context should handle this");
      // Don't clear localStorage here - let the auth context handle it
      // Only redirect in production to avoid development issues
      if (!IS_DEVELOPMENT && !window.location.pathname.includes('/login')) {
        console.log("🔄 Redirecting to login due to 401 error");
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// Export API instances - no mock data, real API only
export const authApi = new AuthApi(api);
export const casesApi = new CasesApi(api);
export const adminApi = new AdminApi(api);
export const reviewerApi = new ReviewerApi(api);

export default api;
